// TanStack-Query-backed `Session` impl. The host app owns a single
// `QueryClient`; this session prefixes every query it writes with
// `['chat-session', sessionId, ...userKey]` so cross-tool memoization
// still composes with the rest of the plugin's cache (an instance
// fetch in qkg shares its cached result with any other tool keying off
// the same prefix), while `invalidate(pattern)` cannot clobber the
// host's non-chat queries. Different `createChatSession` instances get
// independent `sessionId`s so two conversations don't share fetch
// state — matching `createInMemorySession`'s "each call is an
// independent session" semantics.

import type { QueryClient } from '@tanstack/react-query';

import { createBindingStore } from '#core/lib/expr/session/bindingStore.ts';
import type { Session } from '#core/lib/expr/session/Session.ts';

type CreateChatSessionOptions = {
  readonly queryClient: QueryClient;
  /**
   * Stable per-conversation id. Used as the session `id` AND the
   * TanStack query-key prefix, so a thread switch (new id) gets an
   * independent cache namespace. Defaults to a random UUID for
   * non-thread-scoped callers (tests, ad-hoc sessions).
   */
  readonly sessionId?: string;
  /**
   * The registered-op name set, forwarded to `createBindingStore` so a
   * binding that shadows an op name throws `ShadowingOpError`.
   */
  readonly reservedNames?: ReadonlySet<string>;
};

/**
 * Sentinel placed at the front of every query key written by a chat
 * session. Lets `invalidate` target only this session's namespace —
 * never the host's. Plural exported for tests that need to assert
 * key shape; not part of the public API.
 */
export const CHAT_SESSION_QUERY_KEY_PREFIX = 'chat-session';

/**
 * Build a fresh TanStack-Query-backed `Session`.
 *
 * `fetch` routes through `queryClient.fetchQuery`, inheriting:
 * - **Memoization** — cached result returned for repeat keys.
 * - **Concurrent-fetch coalescing** — TanStack natively dedupes
 *   in-flight requests on the same key.
 * - **Cross-tool composition** — the same QueryClient backs every
 *   other plugin-chat hook (`useEvalExpr`, `useMaterialize`,
 *   `useMaterializedExpr`, …), so a session fetch on key `[A, B]`
 *   populates the cache for any non-session reader keyed off the same
 *   `['chat-session', sessionId, A, B]` shape.
 *
 * Failed fetches are removed from the cache (`removeQueries`) on
 * rejection — same semantics as `createInMemorySession`: a retry
 * triggers a fresh fetcher invocation, errors don't poison the
 * cache.
 *
 * `invalidate(pattern)` calls `removeQueries` with the prefixed key
 * (matching by structural prefix, TanStack's default for array
 * queryKeys). `invalidate(undefined)` removes every query in this
 * session's namespace.
 *
 * Bindings (`getBinding` / `recordBinding` / `bindingNames` /
 * `exportRecipes` / `resetBindings` / `subscribe`) are composed from
 * the shared `createBindingStore` helper — same store the in-memory
 * impl uses. Bindings are immutable + recipe-backed *between resets*
 * and live in a plain in-memory Map alongside the QueryClient; they do
 * not participate in the TanStack cache. The session never persists
 * them itself: they are a projection of the active message branch,
 * reconciled by `<SessionHydrationGate>` via `recordBinding` /
 * `resetBindings` (the transcript is the single source of truth — no
 * separate recipe log to drift on regenerate / reload).
 *
 * `sessionId` is the conversation thread id (not always random
 * anymore): it is the session `id` AND the query-key prefix, so a
 * thread switch gets an independent cache namespace. Falls back to a
 * random UUID for callers that are not thread-scoped (tests, ad-hoc
 * sessions).
 */
export const createChatSession = ({
  queryClient,
  sessionId = crypto.randomUUID(),
  reservedNames,
}: CreateChatSessionOptions): Session => {
  // `crypto.randomUUID()` (the default above) is available in every
  // modern browser and in happy-dom (the test environment); no
  // polyfill required.
  const prefix = (key: readonly unknown[]): readonly unknown[] => [CHAT_SESSION_QUERY_KEY_PREFIX, sessionId, ...key];

  const bindingStore = createBindingStore(reservedNames);

  // Promise-reference cache layered atop `queryClient.fetchQuery` —
  // see `Session.fetch`'s contract: repeat calls with the same key
  // must return the SAME Promise reference (Suspense consumers like
  // `use(session.fetch(...))` require it). `fetchQuery` itself
  // returns a fresh outer promise per call, so we memoise the
  // promise here. Cleared on rejection (reject-clears-cache) and on
  // `invalidate(...)` with a matching prefix.
  const promiseCache = new Map<string, Promise<unknown>>();
  const cacheKeyHash = (queryKey: readonly unknown[]): string => JSON.stringify(queryKey);

  return {
    id: sessionId,
    fetch: <T>(key: readonly unknown[], fetcher: () => Promise<T>): Promise<T> => {
      const queryKey = prefix(key);
      const h = cacheKeyHash(queryKey);
      const existing = promiseCache.get(h);
      if (existing !== undefined) {
        return existing as Promise<T>;
      }
      const promise = queryClient
        .fetchQuery({
          queryKey,
          queryFn: fetcher,
          // The Session contract is "return cached if present;
          // otherwise call the fetcher" — match by making the
          // session-written query never stale of its own accord.
          // Independent observers (e.g. a future `useQuery` consumer
          // on this key) still respect their own staleTime. The
          // session re-fetches only when `invalidate` removes the
          // entry, mirroring `createInMemorySession`'s semantics.
          staleTime: Number.POSITIVE_INFINITY,
          // Fail fast. Session fetches wrap LLM-authored, deterministic
          // operations (a CDF query built from an s-expression). The
          // app QueryClient sets no `retry` default, so without this
          // every failure is retried 3× with exponential backoff —
          // turning a deterministic error into a multi-second pending
          // promise that the agent loop laps (repeated identical tool
          // calls, no result surfaced) until the retryer finally
          // exhausts. A deterministic failure must reach the tool's
          // error envelope immediately; the agent re-issues if it's
          // genuinely transient. Matches `createInMemorySession` (no
          // retry).
          retry: false,
        })
        .catch((err) => {
          // Failed fetches don't poison the cache — match the
          // in-memory impl's "reject-clears-cache" semantics so a
          // retry invokes the fetcher fresh rather than replaying
          // the cached error. Both layers (our promise cache + the
          // queryClient's internal cache) clear so the next call
          // produces a brand-new fetcher invocation.
          promiseCache.delete(h);
          queryClient.removeQueries({ queryKey });
          throw err;
        });
      promiseCache.set(h, promise);
      return promise;
    },
    invalidate: (pattern) => {
      const queryKey = prefix(pattern ?? []);
      // Clear any of our cached promises whose stringified queryKey
      // begins with the invalidation prefix (matches TanStack's
      // structural-prefix semantics for `removeQueries`).
      const prefixStr = cacheKeyHash(queryKey).slice(0, -1);
      for (const k of promiseCache.keys()) {
        if (k.startsWith(prefixStr)) {
          promiseCache.delete(k);
        }
      }
      queryClient.removeQueries({ queryKey });
    },
    ...bindingStore,
  };
};
